/**
 * Ledger sync for the admin "Mark paid" action (COL-227).
 *
 * Historically the Reminders mark-paid wrote ONLY the booking columns
 * (payment_status / last_payment_received_at), so the payments ledger —
 * which drives PaymentsHub, the tenant profile Payments tab, the tenant
 * Dashboard and revenue analytics — still showed the month unpaid. This
 * helper records the same fact in the ledger, through the proof-gated
 * pathway every other surface already trusts (#743):
 *
 *   1. ensure the current-period rent payment row exists,
 *   2. store a small admin-attestation file as its payment proof,
 *   3. insert the proof as `approved` — the mark_payment_paid_from_proof
 *      trigger then flips the payment row to paid.
 *
 * Call it BEFORE the booking-columns update so a failure surfaces to the
 * admin instead of silently recreating the drift. Idempotent: an already
 * paid month is left untouched, and re-runs just re-approve the proof.
 */
import { supabase } from "./supabase";
import {
  ensureRentPaymentForMonth,
  type RentPaymentRow,
} from "./payment-booking";
import { reportOwnerPaymentNotification } from "./owner-payment-notify-report";
import { currentPeriodDueDate } from "./rent-reminder-templates";
import { isPaymentEffectivelyPaid, latestProofByPaymentId } from "./rent";

const PROOF_BUCKET = "contracts";

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export async function recordManualRentPaidInLedger(args: {
  bookingId: number;
  /** Tenant user id — without it a ledger row cannot be attributed; the
   *  caller should still proceed with its booking update. Not required when
   *  `paymentId` is given (the row already exists). */
  tenantUserId: string | null;
  /** Booking's rent due day-of-month (1-31); defaults to the 1st. */
  rentDueDay: number | null;
  monthlyRentEur: number;
  /** Admin performing the action — stamped as the proof reviewer. */
  adminUserId: string | null;
  note?: string;
  /** Settle a PAST billing period instead of the current one (COL-435): the
   *  ISO due date of that month's ledger row. Omit for the current period. */
  periodDueDate?: string | null;
  /** Settle this exact, already-existing ledger row (COL-435 past months).
   *  The row is why the month showed as open, so no upsert is needed — this
   *  is the only way to settle a past month for a manual/shell booking with
   *  no `tenantUserId`, since `ensureRentPaymentForMonth` requires one. */
  paymentId?: string | null;
}): Promise<{ error: Error | null }> {
  const {
    bookingId,
    tenantUserId,
    rentDueDay,
    monthlyRentEur,
    adminUserId,
    note,
    periodDueDate,
    paymentId,
  } = args;

  let payment: RentPaymentRow;
  let fallbackDueDate: Date;
  let fallbackAmountCents: number;

  if (paymentId) {
    const { data, error: readErr } = await supabase
      .from("payments")
      .select("*")
      .eq("id", paymentId)
      .maybeSingle();
    if (readErr) return { error: readErr as unknown as Error };
    if (!data)
      return { error: new Error("Ledger row for that month was not found") };
    payment = data as RentPaymentRow;
    fallbackDueDate = payment.due_date
      ? new Date(payment.due_date)
      : new Date();
    fallbackAmountCents = Number(payment.amount_cents ?? 0);
  } else {
    if (!tenantUserId) {
      // No tenant account attached (manual/shell booking) — nothing to
      // reconcile in the per-user ledger.
      return { error: null };
    }

    const dueDate =
      periodDueDate && Number.isFinite(Date.parse(periodDueDate))
        ? new Date(periodDueDate)
        : currentPeriodDueDate(
            rentDueDay && rentDueDay >= 1 && rentDueDay <= 31 ? rentDueDay : 1
          );
    const amountCents = Math.round(Number(monthlyRentEur || 0) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      return { error: new Error("Booking has no monthly rent amount") };
    }

    const { payment: ensured, error: ensureErr } =
      await ensureRentPaymentForMonth({
        userId: tenantUserId,
        bookingId,
        dueDate: isoDate(dueDate),
        amountCents,
        description: "Rent (marked paid by admin)",
      });
    if (ensureErr || !ensured) {
      return { error: ensureErr ?? new Error("Could not create payment row") };
    }
    payment = ensured;
    fallbackDueDate = dueDate;
    fallbackAmountCents = amountCents;
  }
  // "Settled" is the corroborated rule every surface reads (Stripe reference,
  // approved proof, or admin attestation) — a bare `status = 'paid'` with
  // nothing behind it still lists as open, so it must still get attested
  // below or "Mark paid" would report success and change nothing (COL-435).
  const { data: proofRows, error: proofReadErr } = await supabase
    .from("payment_proofs")
    .select("payment_id, status, created_at")
    .eq("payment_id", payment.id)
    .order("created_at", { ascending: false });
  if (proofReadErr) {
    // Fail safe: an unreadable proof list means "unattested", and the
    // attestation written below is idempotent. Log so it isn't invisible.
    console.warn("payment_proofs read failed:", proofReadErr);
  }
  const proofByPayment = latestProofByPaymentId(
    (proofRows ?? []) as Array<{ payment_id: string; status: string | null }>
  );
  if (
    isPaymentEffectivelyPaid(
      payment as Parameters<typeof isPaymentEffectivelyPaid>[0],
      (id) => proofByPayment.get(id) ?? null
    )
  ) {
    // Already settled — nothing to write, but the month may have been settled
    // by a surface that never notified the owner (which was every Rent-tab
    // path until this change), so still fire the mail. The per-payment
    // idempotency stamp makes this a no-op when it did already go out.
    void reportOwnerPaymentNotification({
      id: String(payment.id),
      type: "rent",
      booking_id: bookingId,
      amount_cents: payment.amount_cents ?? fallbackAmountCents,
      due_date: payment.due_date ?? isoDate(fallbackDueDate),
    });
    return { error: null };
  }

  // The proof-gated paid rule requires an APPROVED proof row; store a tiny
  // attestation file so the proof link in PaymentsHub/Dashboard resolves.
  const stampedNote =
    (note ?? "Marked paid by admin from the Reminders view.") +
    `\nBooking #${bookingId} · payment ${payment.id} · ${new Date().toISOString()}`;
  const path = `payment-proofs/${payment.id}/${Date.now()}-admin-attestation.txt`;
  const blob = new Blob([stampedNote], { type: "text/plain" });
  const { error: upErr } = await supabase.storage
    .from(PROOF_BUCKET)
    .upload(path, blob, { contentType: "text/plain", upsert: false });
  if (upErr) return { error: upErr as unknown as Error };

  const { error: proofErr } = await supabase.from("payment_proofs").insert({
    payment_id: payment.id,
    file_path: path,
    file_name: "Admin attestation.txt",
    file_size: blob.size,
    mime_type: "text/plain",
    status: "approved",
    admin_comment: note ?? "Marked paid by admin (Reminders)",
    reviewed_at: new Date().toISOString(),
    reviewed_by: adminUserId,
  });
  if (proofErr) {
    await supabase.storage.from(PROOF_BUCKET).remove([path]);
    return { error: proofErr as unknown as Error };
  }
  if (payment.status === "paid" && !payment.paid_at) {
    // A legacy row that already claimed "paid" with nothing behind it: the
    // mark_payment_paid_from_proof trigger only fires for non-paid rows, so
    // stamp paid_at here or timelines/analytics keep a gap for this month.
    const { error: stampErr } = await supabase
      .from("payments")
      .update({ paid_at: new Date().toISOString() })
      .eq("id", payment.id)
      .is("paid_at", null);
    if (stampErr) console.warn("paid_at backfill failed:", stampErr);
  }

  // Owner "Rent Payment Received" mail — this path flips the payment to paid
  // via the approved-proof insert above (the mark_payment_paid_from_proof
  // trigger), not from inside an edge function, so it needs the same
  // notify-owner-lifecycle hop as every other client-side mark-paid surface.
  // Fire-and-forget: this helper's job is the ledger write, a failed
  // notification must not fail the "mark paid" action.
  void reportOwnerPaymentNotification({
    id: String(payment.id),
    type: "rent",
    booking_id: bookingId,
    amount_cents: payment.amount_cents ?? fallbackAmountCents,
    due_date: payment.due_date ?? isoDate(fallbackDueDate),
  });

  return { error: null };
}
